import React, { useEffect, useMemo, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Button, Divider, Input, Modal, Select, Space, Tooltip } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  CodeOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  BlockOutlined,
  UndoOutlined,
  RedoOutlined,
  LinkOutlined,
} from "@ant-design/icons";

export type TiptapEditorProps = {
  value?: string; // HTML
  onChange?: (html: string) => void;
  disabled?: boolean;
  minHeight?: number;
  maxHeight?: number;
  resizable?: boolean;
  placeholder?: string;
};

export default function TiptapEditor({
  value = "<p></p>",
  onChange,
  disabled = false,
  minHeight = 140,
  maxHeight,
  resizable = true,
  placeholder = "Write something...",
}: TiptapEditorProps) {
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        link: false, // ปิด link ของ StarterKit
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
      }),
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: value || "<p></p>",
    editable: !disabled,
    onCreate: ({ editor }) => {
      setIsEditorEmpty(editor.isEmpty);
    },
    onUpdate: ({ editor }) => {
      setIsEditorEmpty(editor.isEmpty);
      onChange?.(editor.getHTML());
    },
  });

  // sync ค่า value จากภายนอก (เช่นโหลดจาก API แล้ว setFieldsValue)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "<p></p>") !== current) {
      editor.commands.setContent(value || "<p></p>", { parseOptions: { preserveWhitespace: false } });
      setIsEditorEmpty(editor.isEmpty);
    }
  }, [value, editor]);

  const ToggleBtn = ({
    title,
    icon,
    active,
    onClick,
    btnDisabled,
  }: {
    title: string;
    icon: React.ReactNode;
    active?: boolean;
    onClick: () => void;
    btnDisabled?: boolean;
  }) => (
    <Tooltip title={title}>
      <Button
        type={active ? "primary" : "default"}
        icon={icon}
        onClick={onClick}
        disabled={btnDisabled}
      />
    </Tooltip>
  );

  const openLinkModal = () => {
    if (!editor) return;
    const prevUrl = editor.getAttributes("link")?.href as string | undefined;
    setLinkUrl(prevUrl || "");
    setLinkModalOpen(true);
  };

  const applyLink = () => {
    if (!editor) return;
    const url = linkUrl.trim();

    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      setLinkModalOpen(false);
      return;
    }

    const normalized =
      /^https?:\/\//i.test(url) || /^mailto:/i.test(url) ? url : `https://${url}`;

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: normalized })
      .run();

    setLinkModalOpen(false);
  };

  if (!editor) return null;

  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: 8 }}>
      {/* Toolbar */}
      <div style={{ padding: 8, borderBottom: "1px solid #d9d9d9" }}>
        <Space wrap size={8}>
          <Select
            style={{ width: 140 }}
            value={
              editor.isActive("heading", { level: 1 })
                ? "h1"
                : editor.isActive("heading", { level: 2 })
                  ? "h2"
                  : editor.isActive("heading", { level: 3 })
                    ? "h3"
                    : "p"
            }
            onChange={(v) => {
              if (disabled) return;
              const chain = editor.chain().focus();
              if (v === "p") chain.setParagraph().run();
              if (v === "h1") chain.toggleHeading({ level: 1 }).run();
              if (v === "h2") chain.toggleHeading({ level: 2 }).run();
              if (v === "h3") chain.toggleHeading({ level: 3 }).run();
            }}
            disabled={disabled}
            options={[
              { label: "Paragraph", value: "p" },
              { label: "Heading 1", value: "h1" },
              { label: "Heading 2", value: "h2" },
              { label: "Heading 3", value: "h3" },
            ]}
          />

          <Divider orientation="vertical" />

          <ToggleBtn
            title="Bold"
            icon={<BoldOutlined />}
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            btnDisabled={disabled}
          />
          <ToggleBtn
            title="Italic"
            icon={<ItalicOutlined />}
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            btnDisabled={disabled}
          />
          <ToggleBtn
            title="Strike"
            icon={<StrikethroughOutlined />}
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            btnDisabled={disabled}
          />
          <ToggleBtn
            title="Inline code"
            icon={<CodeOutlined />}
            active={editor.isActive("code")}
            onClick={() => editor.chain().focus().toggleCode().run()}
            btnDisabled={disabled}
          />

          <Divider orientation="vertical" />

          <ToggleBtn
            title="Bullet list"
            icon={<UnorderedListOutlined />}
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            btnDisabled={disabled}
          />
          <ToggleBtn
            title="Ordered list"
            icon={<OrderedListOutlined />}
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            btnDisabled={disabled}
          />
          <ToggleBtn
            title="Blockquote"
            icon={<BlockOutlined />}
            active={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            btnDisabled={disabled}
          />

          <Divider orientation="vertical" />

          <ToggleBtn
            title="Link"
            icon={<LinkOutlined />}
            active={editor.isActive("link")}
            onClick={openLinkModal}
            btnDisabled={disabled}
          />

          <Divider orientation="vertical" />

          <Tooltip title="Undo">
            <Button
              icon={<UndoOutlined />}
              onClick={() => editor.chain().focus().undo().run()}
              disabled={disabled || !editor.can().undo()}
            />
          </Tooltip>
          <Tooltip title="Redo">
            <Button
              icon={<RedoOutlined />}
              onClick={() => editor.chain().focus().redo().run()}
              disabled={disabled || !editor.can().redo()}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Content */}
      <div
        style={{
          padding: 12,
          position: "relative",

          // ✅ กล่องที่ลากยืด/หดได้
          minHeight,
          maxHeight,
          resize: resizable ? "vertical" : "none",
          overflow: "auto",

          // ช่วยให้ cursor/UX ดีขึ้น
          cursor: disabled ? "not-allowed" : "text",
        }}
      >
        {isEditorEmpty && (
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              right: 12,
              color: "#bfbfbf",
              pointerEvents: "none",
            }}
          >
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>

      {/* Link modal */}
      <Modal
        title="Add / Edit Link"
        open={linkModalOpen}
        onCancel={() => setLinkModalOpen(false)}
        onOk={applyLink}
        okText="Apply"
        cancelText="Cancel"
        destroyOnHidden
      >
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12, color: "#666" }}>
            ใส่ URL (เว้นว่าง = ลบลิงก์)
          </div>
          <Input
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onPressEnter={applyLink}
          />
        </div>
      </Modal>

      {/* Minimal styling */}
      <style>
        {`
          .ProseMirror { line-height: 1.6; }
          .ProseMirror:focus { outline: none; }
          .ProseMirror p { margin: 0 0 0.8em; }
          .ProseMirror p:last-child { margin-bottom: 0; }
          .ProseMirror ul, .ProseMirror ol { padding-left: 1.2em; }
          .ProseMirror blockquote { margin: 0.5em 0; padding-left: 0.8em; border-left: 3px solid #d9d9d9; color: rgba(0,0,0,0.75); }
          .ProseMirror code { padding: 0.1em 0.3em; border: 1px solid #f0f0f0; border-radius: 4px; background: rgba(0,0,0,0.03); }
        `}
      </style>
    </div>
  );
}
